import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormHelperText, IconButton, LinearProgress, TextField, Typography } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import { ConfigFieldControl, CoreEvent } from '@sierralabs/core-ui';
import { useUnlinkEntity } from '@sierralabs/entity-ui';
import SiteQueryFieldPicker from '../../common/pickers/SiteQueryFieldPicker';
import { useConfigComponent } from '../../hooks/component/useConfigComponent';
import { useGetQueriesForComponentTree } from '../../hooks/component/useGetQueriesForComponentTree';
import { useQueryValue } from '../../hooks/component/useQueryDisplayValue';
import { useSubscribeComponentChanges } from '../../hooks/component/useSubscribeComponentChanges';
import { useSiteContext } from '../../SiteContext';
import { SiteComponent } from '../SiteComponentPlugin';
import { AssignEntityEvent, AssignEntityWarning, useAssignEntityComponent } from './AssignEntityPlugin';
function isDeepSelected(component, id) {
    return (component.id === id ||
        (component.children || []).some(child => isDeepSelected(child, id)));
}
export const AssignEntityUnlinkIconButton = props => {
    var _a, _b, _c, _d, _e, _f;
    const component = useSubscribeComponentChanges(AssignEntityUnlinkIconButtonPlugin, props);
    const { editing, selectedComponentRef, hoverProps, isHover } = useSiteContext();
    const classes = ['site-assign-entity-unlink-icon-button'];
    if (isHover(component)) {
        classes.push('component-hover');
    }
    const { workflowDisabled } = props.queryValueProps || {};
    const [error, setError] = useState();
    const [removeId, setRemoveId] = useState();
    // get the dialog from assignEntity component
    const assignEntity = useAssignEntityComponent(component);
    const assignDialog = (_a = assignEntity === null || assignEntity === void 0 ? void 0 : assignEntity.children) === null || _a === void 0 ? void 0 : _a.find(c => c.slot === 'dialog');
    const assign = ((_b = assignDialog === null || assignDialog === void 0 ? void 0 : assignDialog.props) === null || _b === void 0 ? void 0 : _b.assign) || {};
    const { isUnlinking, unlinkError, response, unlinkEntities } = useUnlinkEntity(assign);
    const cProps = component.props || {};
    const dialog = cProps.dialog || {};
    // get field values based on configuration
    const itemId = useQueryValue((_c = cProps.idField) === null || _c === void 0 ? void 0 : _c.queryId, (_d = cProps.idField) === null || _d === void 0 ? void 0 : _d.queryField, props.queryValueProps);
    const itemLabel = useQueryValue((_e = cProps.labelField) === null || _e === void 0 ? void 0 : _e.queryId, (_f = cProps.labelField) === null || _f === void 0 ? void 0 : _f.queryField, props.queryValueProps);
    // reveal dialog when component or descendant is selected during edit mode
    const isOpen = !!removeId ||
        !!(editing &&
            selectedComponentRef.current &&
            isDeepSelected(component, selectedComponentRef.current.id));
    const clear = useCallback(() => {
        setError(undefined);
        setRemoveId(undefined);
    }, []);
    useEffect(() => {
        if (!(response === null || response === void 0 ? void 0 : response.ok)) {
            return;
        }
        clear();
        // dispatch event to refresh assignEntity card
        const id = assignEntity === null || assignEntity === void 0 ? void 0 : assignEntity.id;
        id && CoreEvent.dispatch(AssignEntityEvent.Assign, { id });
    }, [response, assignEntity === null || assignEntity === void 0 ? void 0 : assignEntity.id, clear]);
    useEffect(() => {
        setError(unlinkError);
    }, [unlinkError]);
    return (React.createElement(SiteComponent, Object.assign({ plugin: AssignEntityUnlinkIconButtonPlugin }, props),
        React.createElement(IconButton, Object.assign({ disabled: workflowDisabled, className: classes.join(' ') }, hoverProps(component), { onClick: event => {
                event.stopPropagation();
                if (!editing && itemId) {
                    setRemoveId(itemId);
                }
            } }),
            React.createElement(Close, null)),
        React.createElement(Dialog, { open: isOpen, onClose: () => !isUnlinking && clear(), onClick: event => event.stopPropagation() },
            isUnlinking && React.createElement(LinearProgress, null),
            React.createElement(DialogTitle, null, dialog.title || 'Unlink Item'),
            React.createElement(DialogContent, null,
                React.createElement(Typography, { variant: 'body2', gutterBottom: true },
                    "Are you sure you want to unlink ",
                    itemLabel || 'this item',
                    "?"),
                error && (React.createElement(Box, { mt: 2 },
                    React.createElement(Alert, { severity: 'error' }, error.message || 'An error occurred.')))),
            React.createElement(DialogActions, null,
                React.createElement(Button, { disabled: isUnlinking, onClick: event => {
                        event.stopPropagation();
                        clear();
                    } }, "Cancel"),
                React.createElement(Button, { color: 'primary', variant: 'contained', disabled: isUnlinking, onClick: event => {
                        event.stopPropagation();
                        if (removeId) {
                            setError(undefined);
                            unlinkEntities([removeId]);
                        }
                    } }, "Unlink")))));
};
const StyledConfigFieldControl = styled(ConfigFieldControl) `
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;
export const AssignEntityUnlinkIconButtonConfigurator = props => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    const { component, update } = useConfigComponent(props.component);
    const queries = useGetQueriesForComponentTree(component);
    return (React.createElement(React.Fragment, null,
        React.createElement(AssignEntityWarning, { component: component }),
        React.createElement(ConfigFieldControl, null,
            React.createElement(TextField, { autoFocus: true, fullWidth: true, size: 'small', label: 'Name', value: component.name || '', onChange: event => update('name', event.target.value), InputProps: { disableUnderline: true } })),
        React.createElement(ConfigFieldControl, null,
            React.createElement(TextField, { autoFocus: true, fullWidth: true, size: 'small', label: 'Dialog Title', value: ((_b = (_a = component.props) === null || _a === void 0 ? void 0 : _a.dialog) === null || _b === void 0 ? void 0 : _b.title) || '', InputProps: { disableUnderline: true }, onChange: event => {
                    update('props.dialog', { title: event.target.value });
                } })),
        React.createElement(StyledConfigFieldControl, null,
            React.createElement(SiteQueryFieldPicker, { options: queries, label: 'Entity ID Field', queryId: (_d = (_c = component.props) === null || _c === void 0 ? void 0 : _c.idField) === null || _d === void 0 ? void 0 : _d.queryId, queryField: (_f = (_e = component.props) === null || _e === void 0 ? void 0 : _e.idField) === null || _f === void 0 ? void 0 : _f.queryField, onChange: value => {
                    update('props.idField', value.queryId ? value : undefined);
                } }),
            React.createElement(FormHelperText, null, "Select the ID field of the Entity")),
        React.createElement(StyledConfigFieldControl, null,
            React.createElement(SiteQueryFieldPicker, { options: queries, label: 'Label Field', queryId: (_h = (_g = component.props) === null || _g === void 0 ? void 0 : _g.labelField) === null || _h === void 0 ? void 0 : _h.queryId, queryField: (_k = (_j = component.props) === null || _j === void 0 ? void 0 : _j.labelField) === null || _k === void 0 ? void 0 : _k.queryField, onChange: value => {
                    update('props.labelField', value.queryId ? value : undefined);
                } }),
            React.createElement(FormHelperText, null, "Select the field value to display in the confirm remove dialog box"))));
};
export const AssignEntityUnlinkIconButtonPlugin = {
    name: 'Assign Entity Unlink Icon Button',
    type: 'assign-entity-unlink-icon-button',
    component: AssignEntityUnlinkIconButton,
    configurator: AssignEntityUnlinkIconButtonConfigurator
};
//# sourceMappingURL=AssignEntityUnlinkIconButtonPlugin.js.map